import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { TbAlertTriangle, TbTemplate, TbDeviceFloppy, TbCircleCheck } from "react-icons/tb";
import { apiRequest } from "src/services/apiClient";
import { useInvoiceTemplates } from "src/hooks/useInvoiceTemplates";
import {
    insertTemplateDays,
    templateDayCount,
    templateDayLines,
    templateLinesFrom,
    templateLinesFromDays,
    type InvoiceTemplate,
    type TemplateLine,
} from "src/helpers/invoiceTemplates";
import { InsertTemplateDialog, SaveTemplateDialog } from "./TemplateDialogs";
import { useGoaCatalog } from "src/hooks/useGoaCatalog";
import { useJustifications, type JustificationList } from "src/hooks/useJustifications";
import { GoaLineEditor } from "src/components/GoaLineEditor/GoaLineEditor";
import { GoaLineIssueBanners } from "src/components/GoaLineEditor/GoaLineIssueBanners";
import { useGoaLineIssues } from "src/components/GoaLineEditor/useGoaLineIssues";
import { blankDraftRow, draftTotals, type GoaLineDraft } from "src/components/GoaLineEditor/goaLineDraft";
import type { ServicesByDay, ServicesCheck } from "./servicesRules";
import { SuggestionList, type SuggestionListProps } from "./StepCheck";

export const formatEuro = (n: number) =>
    n.toLocaleString("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 });

const dayLabel = (iso: string, language: string) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString(language === "en" ? "en-GB" : "de-DE", {
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });

/** Step 3: the GOÄ services, entered per treatment day (same per-day editor as the invoice page). */
export function StepServices({
    days,
    services,
    onChange,
    check,
    suggestions,
    onWarnings,
}: {
    days: string[];
    services: ServicesByDay;
    onChange: (services: ServicesByDay) => void;
    check: ServicesCheck;
    /** Plus+: codes that usually go with the entered diagnoses (only those — step 4 has the rest). */
    suggestions?: SuggestionListProps;
    /** How many warning banners a day shows, for the sign on *Weiter*. Has to be stable. */
    onWarnings?: (day: string, count: number) => void;
}) {
    const { t } = useTranslation();
    const { catalogList, catalogByNr } = useGoaCatalog();
    const justifications = useJustifications();
    const templates = useInvoiceTemplates();

    // First visit with nothing entered: start the first day with one open, empty line, so the
    // code field is right there. It's added after the wizard focused the heading, so it keeps focus.
    const [starter] = useState<GoaLineDraft | null>(() => (days.length > 0 && days.every((d) => !(services[d]?.length)) ? blankDraftRow() : null));
    useEffect(() => {
        if (starter) onChange({ ...services, [days[0]]: [starter] });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const hasProblems = Object.keys(check.problems).length > 0 || check.emptyDays.length > 0;
    // Templates can span several treatment days: saving may take all days, inserting fills the days from here on.
    const allDaysLines = templateLinesFromDays(days.map((d) => services[d] ?? []));
    const insertFrom = (day: string, template: InvoiceTemplate) => {
        const result = insertTemplateDays(services, days, day, template);
        onChange(result.services);
        apiRequest(`/invoice-templates/${template.id}/use`, { method: "POST" }).catch(() => {});
        return result;
    };

    return (
        <div className="ic-services">
            {suggestions && <SuggestionList {...suggestions} titleKey="diagnosisTitle" />}
            {days.map((day) => (
                <DayServices
                    key={day}
                    day={day}
                    rows={services[day] ?? []}
                    onRowsChange={(rows) => onChange({ ...services, [day]: rows })}
                    catalogList={catalogList}
                    catalogByNr={catalogByNr}
                    justifications={justifications}
                    check={check}
                    initialExpandedId={starter && day === days[0] ? starter._localId : null}
                    templates={templates}
                    allDaysLines={allDaysLines}
                    onInsert={(template) => insertFrom(day, template)}
                    onWarnings={onWarnings}
                />
            ))}

            {hasProblems && check.rowCount > 0 && (
                <div className="ic-note ic-note--warn" role="status">
                    <TbAlertTriangle aria-hidden />
                    <span>{t("invoiceCreate.services.fixToContinue")}</span>
                </div>
            )}
        </div>
    );
}

function DayServices({
    day,
    rows,
    onRowsChange,
    catalogList,
    catalogByNr,
    justifications,
    check,
    initialExpandedId,
    templates,
    allDaysLines,
    onInsert,
    onWarnings,
}: {
    day: string;
    rows: GoaLineDraft[];
    onRowsChange: (rows: GoaLineDraft[]) => void;
    catalogList: ReturnType<typeof useGoaCatalog>["catalogList"];
    catalogByNr: ReturnType<typeof useGoaCatalog>["catalogByNr"];
    justifications: JustificationList;
    check: ServicesCheck;
    initialExpandedId: string | null;
    templates: ReturnType<typeof useInvoiceTemplates>;
    /** Every treatment day's lines as one multi-day template — offered when more than one day has lines. */
    allDaysLines: TemplateLine[];
    /** Inserts a template starting at this day (later template days go to the following treatment days). */
    onInsert: (template: InvoiceTemplate) => { insertedDays: number; skippedDays: number };
    onWarnings?: (day: string, count: number) => void;
}) {
    const { t, i18n } = useTranslation();
    // Templates (stories 13/14): insert from this day on, or keep this day's (or all days') lines as a template.
    const [templateDialog, setTemplateDialog] = useState<"insert" | "save" | null>(null);
    const [templateNote, setTemplateNote] = useState<{ text: string; warn?: string } | null>(null);
    const linesToSave = templateLinesFrom(rows);
    const pickTemplate = (template: InvoiceTemplate) => {
        const { insertedDays, skippedDays } = onInsert(template);
        setTemplateDialog(null);
        setTemplateNote({
            text: insertedDays > 1
                ? t("invoiceTemplates.insertedDays", { name: template.name, count: insertedDays })
                : t("invoiceTemplates.inserted", { name: template.name, count: templateDayLines(template, 0).length }),
            warn: skippedDays > 0 ? t("invoiceTemplates.skippedDays", { count: skippedDays }) : undefined,
        });
    };
    // Duplicates on this day (all plans, a warning) + "not billable together" / frequency caps (Pro
    // rules, empty otherwise) — live, before anything is saved.
    const issues = useGoaLineIssues(rows);
    const warningCount = issues.duplicateCodes.size + issues.conflicts.length + issues.maxCountViolations.length;
    useEffect(() => {
        onWarnings?.(day, warningCount);
        return () => onWarnings?.(day, 0);
    }, [onWarnings, day, warningCount]);
    const subtotal = draftTotals(rows).total;
    const empty = check.emptyDays.includes(day);

    const rowIssues = useMemo(() => {
        const out: Record<string, string[]> = {};
        for (const row of rows) {
            const problems = check.problems[row._localId];
            if (problems?.length) out[row._localId] = problems.map((p) => t(`invoiceCreate.services.problems.${p}`));
        }
        return out;
    }, [rows, check, t]);

    return (
        <section className="ic-day-services" aria-labelledby={`ic-day-${day}`}>
            <header className="ic-day-services__header">
                <h3 id={`ic-day-${day}`} className="ic-day-services__title">
                    {t("invoiceCreate.services.dayTitle", { date: dayLabel(day, i18n.language) })}
                </h3>
                <div className="ic-day-services__tools">
                    <button type="button" className="ic-day-services__tool" onClick={() => { setTemplateNote(null); setTemplateDialog("insert"); }}>
                        <TbTemplate aria-hidden /> {t("invoiceTemplates.insert")}
                    </button>
                    <button
                        type="button"
                        className="ic-day-services__tool"
                        disabled={linesToSave.length === 0}
                        title={linesToSave.length === 0 ? t("invoiceTemplates.saveNeedsLines") : undefined}
                        onClick={() => { setTemplateNote(null); setTemplateDialog("save"); }}
                    >
                        <TbDeviceFloppy aria-hidden /> {t("invoiceTemplates.saveAs")}
                    </button>
                </div>
                <span className="ic-day-services__subtotal">{formatEuro(subtotal)}</span>
            </header>
            {templateNote && (
                <p className="ic-day-services__note" role="status">
                    <TbCircleCheck aria-hidden /> {templateNote.text}
                </p>
            )}
            {templateNote?.warn && (
                <p className="ic-day-services__note ic-day-services__note--warn" role="status">
                    <TbAlertTriangle aria-hidden /> {templateNote.warn}
                </p>
            )}
            {templateDialog === "insert" && (
                <InsertTemplateDialog
                    templates={templates.templates}
                    loading={templates.loading}
                    onPick={pickTemplate}
                    onClose={() => setTemplateDialog(null)}
                />
            )}
            {templateDialog === "save" && (
                <SaveTemplateDialog
                    lines={linesToSave}
                    allDaysLines={templateDayCount({ lines: allDaysLines }) > 1 ? allDaysLines : undefined}
                    onSaved={(template) => {
                        setTemplateDialog(null);
                        setTemplateNote({ text: t("invoiceTemplates.saved", { name: template.name }) });
                    }}
                    onClose={() => setTemplateDialog(null)}
                />
            )}
            <GoaLineIssueBanners issues={issues} />
            {empty && <p className="ic-day-services__empty">{t("invoiceCreate.services.emptyDay")}</p>}
            <GoaLineEditor
                rows={rows}
                onRowsChange={onRowsChange}
                catalogList={catalogList}
                catalogByNr={catalogByNr}
                duplicateCodes={issues.duplicateCodes}
                priceEditable
                rowIssues={rowIssues}
                initialExpandedId={initialExpandedId}
                focusInitialRow
                showAdded
                reorderable
                justifications={justifications}
            />
        </section>
    );
}

/** Text for the step's card in the overview row. */
export function servicesSummary(check: ServicesCheck, t: TFunction) {
    if (check.rowCount === 0) return null;
    return (
        <>
            <strong className="ic-card__strong">{formatEuro(check.totals.total)}</strong>
            <span>{t("invoiceCreate.services.summaryLines", { count: check.rowCount })}</span>
            {check.totals.material > 0 && <span>{t("invoiceCreate.services.summaryMaterial", { amount: formatEuro(check.totals.material) })}</span>}
        </>
    );
}
